/**
 * Design tokens — the CSS custom properties in `tokens.css` are the runtime source; this module
 * names them for tests and programmatic use, and records the contrast contract those values must
 * keep in both themes.
 *
 * `DESIGN_TOKENS` is the public inventory. `COLOR_TOKENS` and `ELEVATION_TOKENS` are the
 * theme-scoped tokens: every one of them must be declared by the light block *and* by both dark
 * overrides in `tokens.css` (`tokens.test.ts` enforces it).
 */

export const COLOR_TOKENS = [
  // Surfaces
  '--op-color-bg',
  '--op-color-surface',
  '--op-color-surface-raised',
  '--op-color-surface-sunken',
  '--op-color-overlay',
  /* The paper surface: the page a signature is drawn on and the page a PDF is rendered from. It
   * stays light in both themes, because the marks on it are ink — the same reason a printed page
   * does not invert. It is a colour token rather than a literal so a surface can re-declare it. */
  '--op-color-paper',
  // Text
  '--op-color-text',
  '--op-color-heading',
  '--op-color-muted',
  '--op-color-text-muted',
  '--op-color-subtle',
  '--op-color-on-accent',
  // Borders
  '--op-color-border',
  '--op-color-border-strong',
  // Accent (interactive) — `primary`/`primary-text` are the pre-existing aliases kept for the
  // surfaces that already use them.
  '--op-color-accent',
  '--op-color-accent-hover',
  '--op-color-accent-active',
  '--op-color-accent-subtle',
  '--op-color-accent-text',
  '--op-color-primary',
  '--op-color-primary-text',
  '--op-color-quiet',
  // Deep navy: the second primary action (Send for signature) and other strong, non-accent fills.
  '--op-color-deep',
  '--op-color-deep-hover',
  '--op-color-deep-active',
  '--op-color-on-deep',
  // Status
  '--op-color-success',
  '--op-color-success-subtle',
  '--op-color-success-text',
  '--op-color-warning',
  '--op-color-warning-subtle',
  '--op-color-warning-text',
  '--op-color-danger',
  '--op-color-danger-subtle',
  '--op-color-danger-text',
  '--op-color-info',
  '--op-color-info-subtle',
  '--op-color-info-text',
  // Focus and loading
  '--op-color-focus',
  '--op-color-skeleton',
  '--op-color-skeleton-highlight',
  // Disabled controls
  '--op-color-disabled',
  '--op-color-disabled-text',
  // Signer colours: on the white page (paper) and on app surfaces (chrome)
  '--op-color-signer-paper-1',
  '--op-color-signer-paper-2',
  '--op-color-signer-paper-3',
  '--op-color-signer-paper-4',
  '--op-color-signer-paper-5',
  '--op-color-signer-paper-6',
  '--op-color-signer-1',
  '--op-color-signer-2',
  '--op-color-signer-3',
  '--op-color-signer-4',
  '--op-color-signer-5',
  '--op-color-signer-6',
  // Chart series 1 and 2
  '--op-color-chart-1',
  '--op-color-chart-2',
] as const

export const ELEVATION_TOKENS = [
  '--op-shadow-xs',
  '--op-shadow-sm',
  '--op-shadow-md',
  '--op-shadow-lg',
  '--op-shadow-sheet',
  // Bottom sheets and bars cast their shadow upward
  '--op-shadow-sheet-up',
  '--op-shadow-sheet-up-strong',
] as const

/** Tokens that must resolve in the light theme and in both dark overrides. */
export const THEMED_TOKENS = [...COLOR_TOKENS, ...ELEVATION_TOKENS] as const

export const SPACE_TOKENS = [
  '--op-space-0',
  '--op-space-px',
  '--op-space-1',
  '--op-space-2',
  '--op-space-3',
  '--op-space-4',
  '--op-space-5',
  '--op-space-6',
  '--op-space-7',
  '--op-space-8',
  '--op-space-9',
  '--op-space-10',
  '--op-space-11',
] as const

export const RADIUS_TOKENS = [
  '--op-radius-xs',
  '--op-radius-sm',
  '--op-radius-md',
  '--op-radius-lg',
  '--op-radius-xl',
  '--op-radius-full',
] as const

export const TYPOGRAPHY_TOKENS = [
  '--op-font-family',
  '--op-font-family-mono',
  '--op-font-size-xs',
  '--op-font-size-sm',
  '--op-font-size-md',
  '--op-font-size-lg',
  '--op-font-size-xl',
  '--op-font-size-2xl',
  '--op-font-size-3xl',
  '--op-line-height-tight',
  '--op-line-height-snug',
  '--op-line-height-normal',
  '--op-line-height-relaxed',
  '--op-font-weight-regular',
  '--op-font-weight-medium',
  '--op-font-weight-semibold',
  '--op-font-weight-bold',
  '--op-tracking-tight',
  '--op-tracking-normal',
  '--op-tracking-wide',
] as const

export const CONTROL_TOKENS = [
  '--op-border-width',
  '--op-control-height-sm',
  '--op-control-height-md',
  '--op-control-height-lg',
  '--op-control-check-size',
] as const

export const FOCUS_TOKENS = ['--op-focus-ring-width', '--op-focus-ring-offset'] as const

export const MOTION_TOKENS = [
  '--op-duration-fast',
  '--op-duration-base',
  '--op-duration-shimmer',
  '--op-ease-out',
  '--op-transition-fast',
  '--op-transition-base',
] as const

export const DESIGN_TOKENS = {
  color: COLOR_TOKENS,
  surface: [
    '--op-color-bg',
    '--op-color-surface',
    '--op-color-surface-raised',
    '--op-color-surface-sunken',
    '--op-color-overlay',
    '--op-color-paper',
  ],
  text: [
    '--op-color-text',
    '--op-color-heading',
    '--op-color-muted',
    '--op-color-text-muted',
    '--op-color-subtle',
    '--op-color-on-accent',
  ],
  border: ['--op-color-border', '--op-color-border-strong'],
  accent: [
    '--op-color-accent',
    '--op-color-accent-hover',
    '--op-color-accent-active',
    '--op-color-accent-subtle',
    '--op-color-accent-text',
    '--op-color-primary',
    '--op-color-primary-text',
    '--op-color-quiet',
  ],
  deep: [
    '--op-color-deep',
    '--op-color-deep-hover',
    '--op-color-deep-active',
    '--op-color-on-deep',
  ],
  status: [
    '--op-color-success',
    '--op-color-success-subtle',
    '--op-color-success-text',
    '--op-color-warning',
    '--op-color-warning-subtle',
    '--op-color-warning-text',
    '--op-color-danger',
    '--op-color-danger-subtle',
    '--op-color-danger-text',
    '--op-color-info',
    '--op-color-info-subtle',
    '--op-color-info-text',
  ],
  focus: ['--op-color-focus', '--op-color-skeleton', '--op-color-skeleton-highlight'],
  disabled: ['--op-color-disabled', '--op-color-disabled-text'],
  signer: [
    '--op-color-signer-paper-1',
    '--op-color-signer-paper-2',
    '--op-color-signer-paper-3',
    '--op-color-signer-paper-4',
    '--op-color-signer-paper-5',
    '--op-color-signer-paper-6',
    '--op-color-signer-1',
    '--op-color-signer-2',
    '--op-color-signer-3',
    '--op-color-signer-4',
    '--op-color-signer-5',
    '--op-color-signer-6',
  ],
  chart: ['--op-color-chart-1', '--op-color-chart-2'],
  space: SPACE_TOKENS,
  radius: RADIUS_TOKENS,
  typography: TYPOGRAPHY_TOKENS,
  elevation: ELEVATION_TOKENS,
  control: CONTROL_TOKENS,
  focusRing: FOCUS_TOKENS,
  motion: MOTION_TOKENS,
} as const

/** A foreground/background pair that must keep a minimum WCAG 2.2 contrast ratio in every theme. */
export interface ContrastRequirement {
  readonly fg: string
  readonly bg: string
  /** 4.5 for text, 3 for non-text (borders, indicators, focus rings) per WCAG 1.4.3 / 1.4.11. */
  readonly min: number
  readonly why: string
}

/**
 * The contrast contract of the palette. Both themes are held to it, so a new dark value cannot
 * quietly land a pair below AA.
 */
export const CONTRAST_REQUIREMENTS: readonly ContrastRequirement[] = [
  // Body and heading text on every surface it is designed to sit on.
  { fg: '--op-color-text', bg: '--op-color-bg', min: 4.5, why: 'body text on page' },
  { fg: '--op-color-text', bg: '--op-color-surface', min: 4.5, why: 'body text on card' },
  {
    fg: '--op-color-text',
    bg: '--op-color-surface-raised',
    min: 4.5,
    why: 'body text on raised card',
  },
  {
    fg: '--op-color-text',
    bg: '--op-color-surface-sunken',
    min: 4.5,
    why: 'body text on sunken panel',
  },
  { fg: '--op-color-text', bg: '--op-color-quiet', min: 4.5, why: 'body text on quiet button' },
  { fg: '--op-color-heading', bg: '--op-color-bg', min: 4.5, why: 'headings on page' },
  { fg: '--op-color-heading', bg: '--op-color-surface', min: 4.5, why: 'headings on card' },
  { fg: '--op-color-muted', bg: '--op-color-bg', min: 4.5, why: 'secondary text on page' },
  { fg: '--op-color-muted', bg: '--op-color-surface', min: 4.5, why: 'secondary text on card' },
  {
    fg: '--op-color-muted',
    bg: '--op-color-surface-raised',
    min: 4.5,
    why: 'secondary text on raised card',
  },
  {
    fg: '--op-color-muted',
    bg: '--op-color-surface-sunken',
    min: 4.5,
    why: 'secondary text on sunken panel',
  },
  { fg: '--op-color-subtle', bg: '--op-color-surface', min: 4.5, why: 'hints and meta on card' },
  { fg: '--op-color-subtle', bg: '--op-color-bg', min: 4.5, why: 'hints and meta on page' },
  // Accent: label on a filled control, and accent text on its subtle background.
  { fg: '--op-color-on-accent', bg: '--op-color-accent', min: 4.5, why: 'primary button label' },
  {
    fg: '--op-color-on-accent',
    bg: '--op-color-accent-hover',
    min: 4.5,
    why: 'primary button label (hover)',
  },
  {
    fg: '--op-color-on-accent',
    bg: '--op-color-accent-active',
    min: 4.5,
    why: 'primary button label (active)',
  },
  { fg: '--op-color-on-accent', bg: '--op-color-danger', min: 4.5, why: 'danger button label' },
  { fg: '--op-color-on-deep', bg: '--op-color-deep', min: 4.5, why: 'deep button label' },
  {
    fg: '--op-color-on-deep',
    bg: '--op-color-deep-hover',
    min: 4.5,
    why: 'deep button label (hover)',
  },
  {
    fg: '--op-color-on-deep',
    bg: '--op-color-deep-active',
    min: 4.5,
    why: 'deep button label (active)',
  },
  { fg: '--op-color-accent-text', bg: '--op-color-bg', min: 4.5, why: 'links on page' },
  {
    fg: '--op-color-text',
    bg: '--op-color-accent-subtle',
    min: 4.5,
    why: 'body text on accent tint',
  },
  { fg: '--op-color-accent-text', bg: '--op-color-surface', min: 4.5, why: 'links on card' },
  {
    fg: '--op-color-accent-text',
    bg: '--op-color-accent-subtle',
    min: 4.5,
    why: 'accent text on accent tint',
  },
  // Status: base colour as text/icon, and the darker status text on the status tint.
  { fg: '--op-color-success', bg: '--op-color-surface', min: 4.5, why: 'success text/icon' },
  {
    fg: '--op-color-success-text',
    bg: '--op-color-success-subtle',
    min: 4.5,
    why: 'success callout text',
  },
  {
    fg: '--op-color-success-text',
    bg: '--op-color-surface',
    min: 4.5,
    why: 'success text on card',
  },
  { fg: '--op-color-warning', bg: '--op-color-surface', min: 4.5, why: 'warning text/icon' },
  {
    fg: '--op-color-warning-text',
    bg: '--op-color-warning-subtle',
    min: 4.5,
    why: 'warning callout text',
  },
  {
    fg: '--op-color-warning-text',
    bg: '--op-color-surface',
    min: 4.5,
    why: 'warning text on card',
  },
  { fg: '--op-color-danger', bg: '--op-color-surface', min: 4.5, why: 'danger text/icon' },
  {
    fg: '--op-color-danger-text',
    bg: '--op-color-danger-subtle',
    min: 4.5,
    why: 'danger callout text',
  },
  { fg: '--op-color-danger-text', bg: '--op-color-surface', min: 4.5, why: 'danger text on card' },
  { fg: '--op-color-info', bg: '--op-color-surface', min: 4.5, why: 'info text/icon' },
  { fg: '--op-color-info-text', bg: '--op-color-info-subtle', min: 4.5, why: 'info callout text' },
  { fg: '--op-color-info-text', bg: '--op-color-surface', min: 4.5, why: 'info text on card' },
  // Non-text: boundaries, indicators and the focus ring (WCAG 1.4.11).
  { fg: '--op-color-border-strong', bg: '--op-color-surface', min: 3, why: 'emphasised border' },
  { fg: '--op-color-border-strong', bg: '--op-color-bg', min: 3, why: 'emphasised border on page' },
  { fg: '--op-color-accent', bg: '--op-color-surface', min: 3, why: 'accent indicator/border' },
  { fg: '--op-color-accent', bg: '--op-color-bg', min: 3, why: 'accent indicator on page' },
  {
    fg: '--op-color-accent',
    bg: '--op-color-quiet',
    min: 3,
    why: 'selected tab segment edge on the tab track',
  },
  { fg: '--op-color-focus', bg: '--op-color-surface', min: 3, why: 'focus ring on card' },
  { fg: '--op-color-focus', bg: '--op-color-bg', min: 3, why: 'focus ring on page' },
  {
    fg: '--op-color-focus',
    bg: '--op-color-surface-raised',
    min: 3,
    why: 'focus ring on raised card',
  },
  // Signer colours: field labels on the white page are text; swatches and the armed row's icon and
  // border on app surfaces are non-text indicators (UX-31).
  {
    fg: '--op-color-signer-paper-1',
    bg: '--op-color-paper',
    min: 4.5,
    why: 'signer 1 field label on the page',
  },
  {
    fg: '--op-color-signer-paper-2',
    bg: '--op-color-paper',
    min: 4.5,
    why: 'signer 2 field label on the page',
  },
  {
    fg: '--op-color-signer-paper-3',
    bg: '--op-color-paper',
    min: 4.5,
    why: 'signer 3 field label on the page',
  },
  {
    fg: '--op-color-signer-paper-4',
    bg: '--op-color-paper',
    min: 4.5,
    why: 'signer 4 field label on the page',
  },
  {
    fg: '--op-color-signer-paper-5',
    bg: '--op-color-paper',
    min: 4.5,
    why: 'signer 5 field label on the page',
  },
  {
    fg: '--op-color-signer-paper-6',
    bg: '--op-color-paper',
    min: 4.5,
    why: 'signer 6 field label on the page',
  },
  { fg: '--op-color-signer-1', bg: '--op-color-surface', min: 3, why: 'signer 1 swatch on card' },
  {
    fg: '--op-color-signer-1',
    bg: '--op-color-surface-raised',
    min: 3,
    why: 'signer 1 swatch on raised card',
  },
  { fg: '--op-color-signer-2', bg: '--op-color-surface', min: 3, why: 'signer 2 swatch on card' },
  {
    fg: '--op-color-signer-2',
    bg: '--op-color-surface-raised',
    min: 3,
    why: 'signer 2 swatch on raised card',
  },
  { fg: '--op-color-signer-3', bg: '--op-color-surface', min: 3, why: 'signer 3 swatch on card' },
  {
    fg: '--op-color-signer-3',
    bg: '--op-color-surface-raised',
    min: 3,
    why: 'signer 3 swatch on raised card',
  },
  { fg: '--op-color-signer-4', bg: '--op-color-surface', min: 3, why: 'signer 4 swatch on card' },
  {
    fg: '--op-color-signer-4',
    bg: '--op-color-surface-raised',
    min: 3,
    why: 'signer 4 swatch on raised card',
  },
  { fg: '--op-color-signer-5', bg: '--op-color-surface', min: 3, why: 'signer 5 swatch on card' },
  {
    fg: '--op-color-signer-5',
    bg: '--op-color-surface-raised',
    min: 3,
    why: 'signer 5 swatch on raised card',
  },
  { fg: '--op-color-signer-6', bg: '--op-color-surface', min: 3, why: 'signer 6 swatch on card' },
  {
    fg: '--op-color-signer-6',
    bg: '--op-color-surface-raised',
    min: 3,
    why: 'signer 6 swatch on raised card',
  },
  // Chart marks are graphics: 3:1 against the card they are drawn on (WCAG 1.4.11).
  { fg: '--op-color-chart-1', bg: '--op-color-surface', min: 3, why: 'chart series 1 on card' },
  { fg: '--op-color-chart-2', bg: '--op-color-surface', min: 3, why: 'chart series 2 on card' },
  // Disabled controls stay readable in both themes (a disabled primary is not a dark slab).
  {
    fg: '--op-color-disabled-text',
    bg: '--op-color-disabled',
    min: 4.5,
    why: 'disabled control label',
  },
  {
    fg: '--op-color-disabled-text',
    bg: '--op-color-surface-sunken',
    min: 4.5,
    why: 'disabled field text',
  },
]
